import { Text } from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";

interface TypedTextProps {
  text: string;
  delay?: number;
  onComplete?: () => void;
  fontFamily?: string;
  fontSize?: string;
  color?: string;
  lineHeight?: number;
  fontWeight?: string;
  noOfLines?: number;
}

export const TypedText = ({
  text,
  delay = 0,
  onComplete,
  fontFamily,
  fontSize,
  color,
  lineHeight,
  fontWeight,
  noOfLines,
}: TypedTextProps) => {
  const [displayedText, setDisplayedText] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);

  // Keep onComplete ref updated to avoid stale closures
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    // Reset state when text changes
    setDisplayedText("");
    setIsComplete(false);
    setShowCursor(true);

    let timeoutId: NodeJS.Timeout;
    let rafId: number;

    // Use requestAnimationFrame to ensure browser has painted
    rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        let currentIndex = 0;
        intervalRef.current = setInterval(() => {
          if (currentIndex < text.length) {
            setDisplayedText(text.slice(0, currentIndex + 1));
            currentIndex++;
          } else {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            setIsComplete(true);
            onCompleteRef.current?.();
          }
        }, 35);
      }, delay);
    });

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [text, delay]);

  // Cursor blink effect
  useEffect(() => {
    if (isComplete) {
      // Hide cursor after typing is complete
      const hideCursorTimeout = setTimeout(() => setShowCursor(false), 500);
      return () => clearTimeout(hideCursorTimeout);
    }
    // Blink while typing
    const blinkInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(blinkInterval);
  }, [isComplete]);

  return (
    <Text
      as="span"
      fontFamily={fontFamily}
      fontSize={fontSize}
      color={color}
      lineHeight={lineHeight}
      fontWeight={fontWeight}
      noOfLines={noOfLines}
    >
      {displayedText}
      <Text
        as="span"
        opacity={showCursor ? 1 : 0}
        transition="opacity 0.1s"
        color={color}
      >
        |
      </Text>
    </Text>
  );
};
